import { CSSProperties, FC, useEffect, useState } from 'react'

import {
  Box,
  Button,
  Flex,
  GreyGrey80,
  GreyGrey100,
  Primary13Regular,
  Primary13SemiBold,
  SimpleModal,
  TextAreaField,
  Text,
  useModalClose
} from '@sequencehq/core-components'

import {
  QuoteRecipientsNotifyList,
  type QuoteRecipientRow
} from 'Cube/view/layouts/quote/components/QuoteRecipientsNotifyList'
import {
  NOTIFY_RECIPIENTS_COPY,
  NOTIFY_RECIPIENTS_MODAL_TEST_ID
} from './ModalNotifyRecipients.constants'
import { useModalNotifyRecipients } from './useModalNotifyRecipients'
import { useNotifiableRecipients } from './useNotifiableRecipients'

export const ButtonNotifyRecipients: FC<{
  disabled?: boolean
  style?: CSSProperties
  'data-testid'?: string
}> = ({ disabled = false, style, 'data-testid': dataTestId }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false)

  const { rows, showCounterSignerLastNote } = useNotifiableRecipients()

  // Everyone has either signed or is waiting their turn, so there is nobody
  // left to write to.
  const hasNobodyToNotify = rows.length === 0

  useEffect(() => {
    if (isOpen && hasNobodyToNotify) {
      setIsOpen(false)
    }
  }, [hasNobodyToNotify, isOpen])

  return (
    <>
      <Button
        variant="secondary"
        style={style}
        disabled={disabled || hasNobodyToNotify}
        data-testid={dataTestId}
        onClick={() => setIsOpen(true)}
      >
        {NOTIFY_RECIPIENTS_COPY.button}
      </Button>

      {isOpen && (
        <ModalNotifyRecipients
          onClose={() => setIsOpen(false)}
          rows={rows}
          showCounterSignerLastNote={showCounterSignerLastNote}
        />
      )}
    </>
  )
}

const ModalNotifyRecipients: FC<{
  onClose: () => void
  rows: QuoteRecipientRow[]
  showCounterSignerLastNote: boolean
}> = ({ onClose, rows, showCounterSignerLastNote }) => {
  const closeModal = useModalClose()

  const {
    selectedIds,
    setSelectedIds,
    message,
    setMessage,
    isSubmitting,
    hasNoRecipients,
    signersUnavailableNote,
    onSubmit
  } = useModalNotifyRecipients({ rows })

  return (
    <SimpleModal
      isOpen
      onClose={() => {
        onClose()
        closeModal?.()
      }}
      data-testid={NOTIFY_RECIPIENTS_MODAL_TEST_ID}
      variant="dialog"
      width="436px"
      title={NOTIFY_RECIPIENTS_COPY.title}
      submitButtonText={NOTIFY_RECIPIENTS_COPY.submitButton}
      submittingButtonText={NOTIFY_RECIPIENTS_COPY.submittingButton}
      submitDisabled={hasNoRecipients}
      disableSubmitOnEnter
      onSubmit={onSubmit}
    >
      <Flex flexDir="column" gap="16px">
        <Text {...Primary13Regular} color={GreyGrey80} lineHeight="18px">
          {NOTIFY_RECIPIENTS_COPY.body}
        </Text>

        <Flex flexDir="column" gap="8px">
          <Box {...Primary13SemiBold} color={GreyGrey100}>
            {NOTIFY_RECIPIENTS_COPY.recipientsLabel}
          </Box>

          <QuoteRecipientsNotifyList
            data-testid={`${NOTIFY_RECIPIENTS_MODAL_TEST_ID}.list`}
            rows={rows}
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            showCounterSignerLastNote={showCounterSignerLastNote}
            signersUnavailableNote={signersUnavailableNote}
            disabled={isSubmitting}
          />
        </Flex>

        <TextAreaField
          data-testid={`${NOTIFY_RECIPIENTS_MODAL_TEST_ID}.message`}
          label={NOTIFY_RECIPIENTS_COPY.messageLabel}
          placeholder={NOTIFY_RECIPIENTS_COPY.messagePlaceholder}
          value={message}
          onChange={value => setMessage(value)}
          isDisabled={isSubmitting}
          minHeight="100px"
          styles={{ label: { color: GreyGrey100 } }}
        />
      </Flex>
    </SimpleModal>
  )
}
